import oraculoLogs from '../../services/oraculo-logs.service';
import { logHttpError } from '../../utils/http-error-logger';
import * as v2 from '@/core/services/api/v2';

/**
 * Autenticação, no backend V2.
 *
 * O que mudou em relação ao Strapi, e por quê:
 *
 * - `POST /api/auth/local` virou `POST /api/auth/login`, e o registro deixou de
 *   ser `/auth/local/register`. Não é renomeação estética: o contrato é outro.
 * - A resposta traz `accessToken` (curto) em vez de `jwt` (longo). O token longo
 *   agora é um refresh em cookie `httpOnly`, que este código nunca enxerga.
 * - `GET /api/users/me` virou `GET /api/auth/me`, e é ele que diz o `playerId`.
 *   Sem esse número o cliente não sabe endereçar nenhuma rota de jogo: o token
 *   identifica o **usuário**, e jogador é outra tabela.
 * - Não existe mais `find-or-create` de jogador. O registro cria conta e jogador
 *   na mesma transação, do lado do servidor.
 */

/**
 * O que buscar depois de entrar.
 *
 * `fishing/fetchAllFishes` saiu da lista: não existe rota no V2 que despeje o
 * bestiário inteiro. O peixe é sorteado no servidor e o cliente descobre qual
 * foi ao pescar — a lista completa era, no Strapi, também a lista do que dá para
 * pescar em cada mapa, e isso agora é informação do servidor.
 *
 * `market/updateMarketPrices` fala com o V2 (`v2.mercado()`); nada daqui usa o
 * Strapi, que foi aposentado. `allSettled` segue absorvendo a falha de qualquer
 * uma: uma carga que cai não pode impedir o jogador de entrar.
 */
const CARGAS_DO_BOOT = [
  { action: 'shop/fetchShopItems', name: 'Shop Items' },
  { action: 'maps/fetchMaps', name: 'maps' },
  { action: 'market/updateMarketPrices', name: 'prices' },
];

async function carregarDadosDoBoot(dispatch, userId) {
  const resultados = await Promise.allSettled(
    CARGAS_DO_BOOT.map(({ action }) => dispatch(action, undefined, { root: true })),
  );

  resultados.forEach((resultado, i) => {
    if (resultado.status === 'rejected') {
      console.warn(`Failed to fetch ${CARGAS_DO_BOOT[i].name}:`, resultado.reason);
      oraculoLogs.warn(`Failed to fetch ${CARGAS_DO_BOOT[i].name}`, {
        userId,
        details: { action: CARGAS_DO_BOOT[i].action, reason: resultado.reason?.message },
      });
    }
  });

  return resultados;
}

export default {
  namespaced: true,
  state: {
    user: null,
    userId: null,
    /** `players.id`. É o que endereça toda rota de jogo. */
    playerId: null,
    loading: false,
    errors: {}
  },
  mutations: {
    SET_AUTH(state, { user, playerId }) {
      state.user = user;
      state.userId = user.id;
      state.playerId = playerId ?? null;
    },
    CLEAR_AUTH(state) {
      state.user = null;
      state.userId = null;
      state.playerId = null;
    },
    AUTH_SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERRORS(state, errors) {
      state.errors = errors;
    },
  },
  actions: {
    /**
     * Entrar.
     *
     * O parâmetro se chama `email` por compatibilidade com as telas, mas o campo
     * do backend é `identifier` e aceita e-mail **ou** nome de usuário.
     */
    async login({ commit, dispatch }, { email, password }) {
      commit('AUTH_SET_LOADING', true);
      commit('SET_ERRORS', {});

      try {
        const sessao = await v2.entrar({ identifier: email, password });
        // O login devolve o usuário, não o jogador; `me` é quem sabe o playerId.
        const { playerId } = await v2.quemSouEu();

        commit('SET_AUTH', { user: sessao.user, playerId });

        try {
          await dispatch('player/initializePlayer', playerId, { root: true });
        } catch (error) {
          console.warn('Player initialization failed:', error);
          oraculoLogs.warn('Player initialization failed after login', {
            userId: sessao.user.id,
            details: { errorMessage: error.message },
            error: oraculoLogs.captureErrorDetails(error)
          });
        }

        await carregarDadosDoBoot(dispatch, sessao.user.id);
        commit('AUTH_SET_LOADING', false);

        oraculoLogs.info('Login successful', {
          userId: sessao.user.id,
          details: { username: sessao.user.username, playerId }
        });

        return sessao;
      } catch (error) {
        commit('AUTH_SET_LOADING', false);

        logHttpError(error, {
          method: 'POST',
          url: '/api/auth/login',
          data: { identifier: email, password: '***' },
          sanitizeData: true
        });

        oraculoLogs.error('Login failed in auth store', {
          details: {
            errorMessage: error.response?.data?.message || error.message,
            statusCode: error.response?.status
          },
          http: { method: 'POST', path: '/api/auth/login', status: error.response?.status || 500 },
          error: oraculoLogs.captureErrorDetails(error)
        });

        throw error;
      }
    },

    /**
     * Cadastrar.
     *
     * `referralId` vira `indication` no corpo — o backend grava a indicação no
     * jogador que ele mesmo cria. Não há mais uma segunda chamada para criar o
     * jogador, e é essa a diferença que importa: no Strapi eram duas escritas
     * soltas, e falhar na segunda deixava conta sem jogador.
     */
    async register({ commit, dispatch }, { username, email, password, referralId }) {
      commit('AUTH_SET_LOADING', true);
      commit('SET_ERRORS', {});

      try {
        const sessao = await v2.registrar({ username, email, password, indication: referralId });
        const { playerId } = await v2.quemSouEu();

        commit('SET_AUTH', { user: sessao.user, playerId });

        try {
          await dispatch('player/initializePlayer', playerId, { root: true });
        } catch (error) {
          console.warn('Player initialization failed:', error);
          oraculoLogs.warn('Player load failed after registration', {
            userId: sessao.user.id,
            details: { username, hasReferral: !!referralId, errorMessage: error.message },
            error: oraculoLogs.captureErrorDetails(error)
          });
        }

        sessionStorage.removeItem('referralId');
        commit('AUTH_SET_LOADING', false);

        oraculoLogs.info('Registration successful', {
          userId: sessao.user.id,
          details: { username, hasReferral: !!referralId, playerId }
        });

        return sessao;
      } catch (error) {
        commit('AUTH_SET_LOADING', false);

        logHttpError(error, {
          method: 'POST',
          url: '/api/auth/register',
          data: { username, email, password: '***' },
          sanitizeData: true
        });

        oraculoLogs.error('Registration failed in auth store', {
          details: {
            username,
            errorMessage: error.response?.data?.message || error.message,
            statusCode: error.response?.status
          },
          http: {
            method: 'POST',
            path: '/api/auth/register',
            status: error.response?.status || 500
          },
          error: oraculoLogs.captureErrorDetails(error)
        });

        throw error;
      }
    },

    /**
     * Recuperação de senha.
     *
     * O V2 não tem essas rotas — sem infraestrutura de e-mail (SMTP) não há
     * como enviar o código de recuperação. Falha explícita em vez de chamada
     * muda: um "enviamos o e-mail" que não enviou nada é pior do que um erro
     * na tela. A troca de senha logado existe (`v2.trocarSenha`, no
     * ConfigsModal); quem esqueceu a senha fala com o suporte.
     */
    /**
     * Esqueci a senha, no V2 via Resend. Sempre resolve: o servidor responde
     * 204 exista a conta ou não, e a tela diz "se houver conta, enviamos" —
     * nunca "conta não encontrada", que confirmaria e-mails a um atacante.
     */
    async forgotPassword({ commit }, { email }) {
      try {
        await v2.esqueciSenha(email);
        commit('SET_ERRORS', { forgotPassword: null });
        return { success: true };
      } catch (error) {
        const mensagem =
          error.response?.data?.message || error.message || 'Falha ao pedir o e-mail';
        commit('SET_ERRORS', { forgotPassword: mensagem });
        throw error;
      }
    },

    async checkPlayerStatus({ state }) {
      return state.playerId ?? null;
    },

    /**
     * Retomar a sessão ao abrir o jogo.
     *
     * Duas fontes possíveis: o access token que sobreviveu ao F5 em
     * `sessionStorage`, ou — se ele expirou ou a aba é nova — o cookie de
     * refresh, que o servidor troca por um par novo. Só depois das duas falharem
     * é que o jogador precisa digitar a senha de novo.
     */
    async autoLogin({ commit, dispatch }) {
      commit('AUTH_SET_LOADING', true);

      try {
        // A mesma promessa que o guard do router espera: uma tentativa de
        // refresh por carregamento, nunca duas.
        if (!(await v2.restaurarSessao())) {
          throw new Error('Sem sessão para retomar');
        }

        const eu = await v2.quemSouEu();
        commit('SET_AUTH', {
          user: { id: eu.id, username: eu.username, role: eu.role },
          playerId: eu.playerId
        });

        try {
          await dispatch('player/initializePlayer', eu.playerId, { root: true });
        } catch (error) {
          console.warn('Player initialization failed:', error);
          oraculoLogs.warn('Player initialization failed in autoLogin', {
            userId: eu.id,
            details: { errorMessage: error.message },
            error: oraculoLogs.captureErrorDetails(error)
          });
        }

        await carregarDadosDoBoot(dispatch, eu.id);
        commit('AUTH_SET_LOADING', false);

        oraculoLogs.info('AutoLogin successful', {
          userId: eu.id,
          details: { username: eu.username, playerId: eu.playerId }
        });

        return true;
      } catch (error) {
        console.error('Auto login failed:', error);

        logHttpError(error, { method: 'GET', url: '/api/auth/me', data: {} });

        v2.limparSessao();
        commit('CLEAR_AUTH');
        commit('AUTH_SET_LOADING', false);

        oraculoLogs.error('AutoLogin failed', {
          details: { errorMessage: error.message, statusCode: error.response?.status },
          http: { method: 'GET', path: '/api/auth/me', status: error.response?.status || 500 },
          error: oraculoLogs.captureErrorDetails(error)
        });

        return false;
      }
    },

    async logout({ commit }) {
      // O servidor derruba a família de refresh e põe o access na denylist; sem
      // essa chamada o refresh continuaria válido até expirar sozinho.
      await v2.sair();
      // Sem isto, a promessa memoizada continuaria dizendo que há sessão e um
      // login novo na mesma aba não seria tentado.
      v2.esquecerRestauracao();
      commit('CLEAR_AUTH');
    },

    async checkAuth({ commit }) {
      if (!v2.temSessao()) {
        commit('CLEAR_AUTH');
        return false;
      }

      try {
        const eu = await v2.quemSouEu();
        commit('SET_AUTH', {
          user: { id: eu.id, username: eu.username, role: eu.role },
          playerId: eu.playerId
        });
        return true;
      } catch (error) {
        console.error('Auth check failed:', error);
        logHttpError(error, { method: 'GET', url: '/api/auth/me', data: {} });
        v2.limparSessao();
        commit('CLEAR_AUTH');
        return false;
      }
    }
  },
  getters: {
    isAuthenticated: state => !!state.user,
    currentUserId: state => state.userId,
    currentPlayerId: state => state.playerId,
    currentUser: state => state.user,
    isLoading: state => state.loading,
    getErrors: state => state.errors
  }
};
